import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { APIProvider, Map, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { Truck } from 'lucide-react';

// Zone colors by category
const ZONE_COLORS = {
  warehouse: { fill: '#22c55e40', stroke: '#16a34a' },
  restricted: { fill: '#ef444440', stroke: '#dc2626' },
  delivery: { fill: '#f59e0b40', stroke: '#d97706' },
  service: { fill: '#3b82f640', stroke: '#2563eb' }
};

// Google Maps wrapper with zones support
const GoogleMap = ({
  apiKey,
  vehicles = [],
  zones = [],
  selectedVehicle,
  onVehicleSelect,
  selectedZone,
  onZoneSelect,
  isDrawing = false,
  drawingType = 'circle',
  drawingPoints = [],
  onPointAdd,
  center = { lat: -23.5505, lng: -46.6333 },
  zoom = 12,
  className = ''
}) => {const { t } = useTranslation("wms");
  const [map, setMap] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);

  // Draw zones on map
  useEffect(() => {
    if (!map || !window.google) return;

    // Clear existing overlays
    // In a real implementation, we'd track these and clear them

    zones.forEach((zone) => {
      const colors = ZONE_COLORS[zone.category] || ZONE_COLORS.service;

      if (zone.type === 'circle' && zone.center) {
        new window.google.maps.Circle({
          map,
          center: { lat: zone.center.lat, lng: zone.center.lng },
          radius: zone.radius || 500,
          fillColor: colors.fill,
          fillOpacity: 0.3,
          strokeColor: colors.stroke,
          strokeWeight: 2,
          clickable: true
        });
      } else if (zone.type === 'polygon' && zone.points?.length > 2) {
        new window.google.maps.Polygon({
          map,
          paths: zone.points.map((p) => ({ lat: p.lat, lng: p.lng })),
          fillColor: colors.fill,
          fillOpacity: 0.3,
          strokeColor: colors.stroke,
          strokeWeight: 2,
          clickable: true
        });
      }
    });
  }, [map, zones]);

  // Handle map click for drawing
  useEffect(() => {
    if (!map || !isDrawing) return;

    const listener = map.addListener('click', (e) => {
      onPointAdd?.({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });

    return () => {
      window.google?.maps.event.removeListener(listener);
    };
  }, [map, isDrawing, onPointAdd]);

  if (!apiKey) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`} style={{ minHeight: '400px' }}>
        <p className="text-gray-500">{t("googleMapsApi", "Google Maps API key required")}</p>
      </div>);

  }

  const statusColors = {
    online: '#22c55e',
    offline: '#9ca3af',
    moving: '#3b82f6',
    idle: '#f59e0b',
    alert: '#ef4444'
  };

  return (
    <APIProvider apiKey={apiKey}>
      <Map
        defaultCenter={center}
        defaultZoom={zoom}
        mapId="geofencing-map"
        className={className}
        style={{ minHeight: '400px' }}
        onCameraChanged={(ev) => {

          // Can track camera changes if needed
        }} gestureHandling="greedy">
        
        {/* Vehicle Markers */}
        {vehicles.map((vehicle) => {
          if (!vehicle.location?.lat || !vehicle.location?.lng) return null;

          return (
            <AdvancedMarker
              key={vehicle.id}
              position={{ lat: vehicle.location.lat, lng: vehicle.location.lng }}
              onClick={() => onVehicleSelect?.(vehicle)}>
              
              <div
                className="cursor-pointer transform transition-all hover:scale-110"
                style={{
                  width: '36px',
                  height: '36px',
                  background: statusColors[vehicle.status] || statusColors.online,
                  borderRadius: '50%',
                  border: '3px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                
                <Truck className="h-5 w-5 text-white" />
              </div>
            </AdvancedMarker>);

        })}

        {/* Info Window for selected vehicle */}
        {selectedVehicle && selectedVehicle.location &&
        <InfoWindow
          position={{ lat: selectedVehicle.location.lat, lng: selectedVehicle.location.lng }}
          onCloseClick={() => onVehicleSelect?.(null)}>
          
            <div className="text-sm p-2">
              <strong>{selectedVehicle.name}</strong>
              <br />{t("status", "Status:")}
            {selectedVehicle.status}
              <br />{t("speed", "Speed:")}
            {selectedVehicle.speed || 0}{t("kmh", "km/h")}
            {selectedVehicle.driver && <><br />{t("driver", "Driver:")}{selectedVehicle.driver}</>}
            </div>
          </InfoWindow>
        }
      </Map>
    </APIProvider>);

};

export default GoogleMap;